import { t } from "@lingui/macro";
import {
  BulkDeleteExpensesDocument,
  type BulkDeleteExpensesMutation,
  type BulkDeleteExpensesMutationVariables,
  ExpenseAbraFlexiSelectedExportDocument,
  type ExpenseAbraFlexiSelectedExportQuery,
  type ExpenseAbraFlexiSelectedExportQueryVariables,
  ExpenseAttachmentsExportBulkDocument,
  type ExpenseAttachmentsExportBulkQuery,
  type ExpenseAttachmentsExportBulkQueryVariables,
  ExpenseInvoiceStatusEnum,
  ExpenseOmegaExportDocument,
  type ExpenseOmegaExportQuery,
  type ExpenseOmegaExportQueryVariables,
  ExpenseOrderByColumnEnum,
  ExpensePremierExportDocument,
  type ExpensePremierExportQuery,
  type ExpensePremierExportQueryVariables,
  type ExpenseStatusEnum,
  type ExpensesSummary,
  ExpensesTableSelectedSummaryDocument,
  type ExpensesTableSelectedSummaryQuery,
  type ExpensesTableSelectedSummaryQueryVariables,
  ExpensesWhereColumn,
  ExpenseTypeEnum,
  type GetExpensesQuery,
  GetExpenseTimeBillingsDocument,
  type GetExpenseTimeBillingsQuery,
  type GetExpenseTimeBillingsQueryVariables,
  MarkExpenseAsUnpaidDocument,
  type MarkExpenseAsUnpaidMutation,
  type MarkExpenseAsUnpaidMutationVariables,
  ModelLockLockableTypeEnum,
  PohodaExpenseExportDocument,
  type PohodaExpenseExportQuery,
  type PohodaExpenseExportQueryVariables,
  SqlOperator,
  TransakcioExpenseExportDocument,
  type TransakcioExpenseExportQuery,
  type TransakcioExpenseExportQueryVariables,
  VarioExpenseExportDocument,
  type VarioExpenseExportQuery,
  type VarioExpenseExportQueryVariables,
} from "@src/__generated__/graphql";
import {
  type ExpenseFilterOptionsQuery,
  ExpensePaymentTypeEnum,
  type PaymentType,
  SortOrder,
  type UsersSimpleMapQuery,
} from "@src/__generated__/urql-graphql";
import { BankStore } from "@src/components/modules/bank/stores";
import type { IOption } from "@src/components/ui-kit";
import { FetchHelper } from "@src/helpers/apollo/fetch";
import { MutationHelper } from "@src/helpers/apollo/mutation";
import { appStore } from "@src/stores/AppStore";
import {
  commonQueryVariables,
  commonSearchParams,
} from "@src/utils/apolloHelpers";
import {
  getBooleanFilterOptions,
  getPaymentStatusFilterOptions,
} from "@src/utils/components/filters/constants";
import { Filter, Filters } from "@src/utils/components/filters/models";
import { contractorOptionsQuery } from "@src/utils/components/filters/paginatedOptions";
import { OrderBy } from "@src/utils/components/sorting/OrderBy";
import { required } from "@src/utils/forms/validators";
import mapToOptions from "@src/utils/map-to-options";
import { DisclosureState } from "@src/utils/mobx/states/DisclosureState";
import { PaginationState } from "@src/utils/mobx/states/PaginationState";
import { FieldState, FormState } from "formstate";
import { camelCase, lowerCase, map, upperFirst } from "lodash";
import { action, computed, makeObservable, observable } from "mobx";
import Router from "next/router";
import { ExpenseModel } from "./models";

class ModalForms {
  bank_account_id = new FieldState<PaymentType["id"] | undefined>(
    undefined,
  ).validators(required);
  card_id = new FieldState<PaymentType["id"] | undefined>(undefined).validators(
    required,
  );
  date = new FieldState<Date>(new Date()).validators(required);
  dateTo = new FieldState<Date>(new Date()).validators(required);

  paymentOrder = new FormState({
    bank_account_id: this.bank_account_id,
    date: this.date,
  });

  qrCodes = new FormState({
    card_id: this.card_id,
    date: this.date,
  });
}

export enum QuickFilters {
  ExpenseItems = "expenseItems",
}

type MarkAsUnpaidModalData = {
  id: string;
  hasMatchedPayments: boolean;
};

export class StoreExpenses {
  refetch?: () => void;
  asExternalModule = false;
  hideColsInExternalModule = [] as string[];
  readonly tableKey = "expenses";
  pagination = new PaginationState(this.tableKey, {
    perPageOptions: [50, 100, 150],
  });
  markAsUnpaidModal = new DisclosureState<MarkAsUnpaidModalData>();

  @observable
  userSimpleMap: Array<{
    id: UsersSimpleMapQuery["userSimpleMap"][0]["id"];
    full_name: UsersSimpleMapQuery["userSimpleMap"][0]["full_name"];
  }> = [];

  @observable
  selectable = true;

  @observable
  bulkActionsLoading = false;

  modalForms = new ModalForms();

  @observable
  activeView: "default" | "expenseItems" = "default";

  @computed
  get isExpenseItemsView() {
    return this.activeView === "expenseItems";
  }

  @action.bound
  setActiveView(view: "default" | "expenseItems", clearQueryParams = true) {
    const commonQueryParams = Object.fromEntries(
      Object.entries(Router.query).filter(
        ([key]) => !["page", "first"].includes(key),
      ),
    );

    if (view === "expenseItems") {
      clearQueryParams &&
        Router.replace({
          query: {
            ...commonQueryParams,
            active: QuickFilters.ExpenseItems,
          },
        });
      setTimeout(() => {
        this.activeView = view;
      }, 100);
      return;
    }

    Router.replace({
      query: Object.fromEntries(
        Object.entries(commonQueryParams).filter(([key]) => key !== "active"),
      ),
    });

    this.activeView = view;
    this.activeQuickFilter?.clear();
  }

  orderBy = new OrderBy<ExpenseOrderByColumnEnum>([
    { column: ExpenseOrderByColumnEnum.Id, order: SortOrder.Desc },
  ]);

  where = new Filters<ExpensesWhereColumn>(
    [
      new Filter({
        column: ExpensesWhereColumn.Type,
        operator: SqlOperator.In,
        title: t`Type`,
        options: map(ExpenseTypeEnum, (type) => {
          if (type === ExpenseTypeEnum.Card)
            return {
              label: t`card payments`,
              value: camelCase(type),
            };

          return {
            label: lowerCase(type),
            value: camelCase(type),
          };
        }),
      }),
      new Filter({
        column: ExpensesWhereColumn.PaymentStatus,
        operator: SqlOperator.In,
        title: t`Payment status`,
        options: getPaymentStatusFilterOptions(),
      }),
      new Filter({
        column: ExpensesWhereColumn.Status,
        hidden: true,
        operator: SqlOperator.Eq,
        title: "",
        options: [],
        value: "",
        skipWhenValueEquals: "",
      }),
      new Filter({
        column: ExpensesWhereColumn.InvoiceStatus,
        operator: SqlOperator.In,
        title: t`Invoice status`,
        options: map(ExpenseInvoiceStatusEnum, (f) => ({
          label: upperFirst(lowerCase(f)),
          value: camelCase(f),
        })),
      }),
      new Filter({
        column: ExpensesWhereColumn.ContractorId,
        isPaginated: true,
        optionsQuery: contractorOptionsQuery,
        operator: SqlOperator.In,
        title: t`Contractor`,
        options: [],
      }),
      new Filter({
        column: ExpensesWhereColumn.BrandId,
        operator: SqlOperator.In,
        title: t`Client/Brand`,
        tagTitle: t`Brand`,
        options: [],
        sectioned: true,
        hasSelectAllOption: true,
      }),
      new Filter({
        column: ExpensesWhereColumn.WorkspaceCompanyId,
        operator: SqlOperator.In,
        title: t`Legal entity`,
        options: [],
      }),
      new Filter({
        column: ExpensesWhereColumn.WorkspaceBankAccountId,
        operator: SqlOperator.In,
        title: t`Agency bank account`,
        options: [],
      }),
      new Filter({
        column: ExpensesWhereColumn.MandatoryId,
        operator: SqlOperator.In,
        title: t`Responsible`,
        options: [],
      }),
      new Filter({
        column: ExpensesWhereColumn.DueDate,
        operator: SqlOperator.Between,
        dateRange: true,
        title: t`Due date`,
        options: [],
      }),
      new Filter({
        column: ExpensesWhereColumn.DateOfSupply,
        operator: SqlOperator.Between,
        dateRange: true,
        title: t`Date of supply`,
        options: [],
        value: [],
      }),
      //    TODO Fix
      //  ee  @ts-expect-error
      //    new Filter({
      //      column: ExpensesWhereColumn.CreatedAt,
      //      operator: SqlOperator.Between,
      //      dateRange: true,
      //      title: t`Created at`,
      //      options: [],
      //      value: [toApiDate(startOfYear(new Date())), toApiDate(endOfYear(new Date()))],
      //    }),
      new Filter({
        column: ExpensesWhereColumn.PaidAt,
        title: t`Paid`,
        operator: SqlOperator.Between,
        dateRange: true,
        options: [],
      }),
      new Filter({
        hidden: !appStore.workspaceStore.hasModuleForPage("time_billing"),
        column: ExpensesWhereColumn.HasTimeBilling,
        title: t`Has time billing`,
        operator: SqlOperator.Eq,
        options: getBooleanFilterOptions(),
        value: [],
      }),
      new Filter({
        column: ExpensesWhereColumn.IsExported,
        operator: SqlOperator.Eq,
        title: t`Exported`,
        options: getBooleanFilterOptions(),
      }),
      new Filter({
        column: ExpensesWhereColumn.IssueDate,
        operator: SqlOperator.Between,
        title: t`Issue date`,
        dateRange: true,
        options: [],
      }),
      new Filter({
        column: ExpensesWhereColumn.ProjectId,
        operator: SqlOperator.In,
        title: t`Project`,
        options: [],
      }),
      new Filter({
        column: ExpensesWhereColumn.AmountFrom,
        operator: SqlOperator.Gte,
        title: t`Amount from (with VAT)`,
        options: [],
        mutateToGql: BankStore.mutateAmountFilter,
      }),
      new Filter({
        column: ExpensesWhereColumn.AmountTo,
        operator: SqlOperator.Lte,
        title: t`Amount to (with VAT)`,
        options: [],
        mutateToGql: BankStore.mutateAmountFilter,
      }),
    ],
    {
      onChange: this.pagination.resetPage,
      tableKey: this.tableKey,
      orderBy: this.orderBy,
    },
  );

  private selectedWhere = new Filters(
    [
      new Filter({
        column: ExpensesWhereColumn.Id,
        operator: SqlOperator.In,
        options: [],
      }),
    ],
    { onChange: () => this.fetchSelectedSummary() },
  );

  @observable.ref
  searchTerm = "";

  @observable.ref
  expenses: ExpenseModel[] = [];

  @observable.ref
  expensesSummary?: Pick<
    ExpensesSummary,
    | "expenseIds"
    | "expenses"
    | "total_vat"
    | "total_amount"
    | "total_amount_with_vat"
    | "payments_due"
    | "payments_due_vat"
    | "payments_due_without_vat"
    | "overdue_by_less_than_30_days"
    | "overdue_by_less_than_30_days_vat"
    | "overdue_by_less_than_30_days_without_vat"
    | "overdue_by_31_to_90_days"
    | "overdue_by_31_to_90_days_vat"
    | "overdue_by_31_to_90_days_without_vat"
    | "overdue_by_91_to_365_days"
    | "overdue_by_91_to_365_days_vat"
    | "overdue_by_91_to_365_days_without_vat"
    | "overdue_by_more_than_365_days"
    | "overdue_by_more_than_365_days_vat"
    | "overdue_by_more_than_365_days_without_vat"
  >;

  @observable.ref
  cardOptions: IOption[] = [];

  @observable.ref
  bankAccountOptions: IOption[] = [];

  @observable.ref
  allExpensesIds: ExpenseModel["id"][] = [];

  /**
   * used in bulk actions
   */
  @observable.ref
  selectedExpensesIds: ExpenseModel["id"][] = [];

  @observable.ref
  selectedSummary?: ExpensesTableSelectedSummaryQuery["expensesSummary"];

  @observable.ref
  initialSelectedIds: ExpenseModel["id"][] = [];

  @observable.ref
  unprocessableExpenseIds: ExpenseModel["id"][] = [];

  varioExportFetcher = new FetchHelper<
    VarioExpenseExportQuery,
    VarioExpenseExportQueryVariables
  >(VarioExpenseExportDocument);

  transakcioExportFetcher = new FetchHelper<
    TransakcioExpenseExportQuery,
    TransakcioExpenseExportQueryVariables
  >(TransakcioExpenseExportDocument);

  private omegaExportFetcher = new FetchHelper<
    ExpenseOmegaExportQuery,
    ExpenseOmegaExportQueryVariables
  >(ExpenseOmegaExportDocument);

  private abraFlexiExportFetcher = new FetchHelper<
    ExpenseAbraFlexiSelectedExportQuery,
    ExpenseAbraFlexiSelectedExportQueryVariables
  >(ExpenseAbraFlexiSelectedExportDocument);

  private premierExportFetcher = new FetchHelper<
    ExpensePremierExportQuery,
    ExpensePremierExportQueryVariables
  >(ExpensePremierExportDocument);

  private bulkDeleteMutator = new MutationHelper<
    BulkDeleteExpensesMutation,
    BulkDeleteExpensesMutationVariables
  >(BulkDeleteExpensesDocument);

  private markAsUnpaidMutator = new MutationHelper<
    MarkExpenseAsUnpaidMutation,
    MarkExpenseAsUnpaidMutationVariables
  >(MarkExpenseAsUnpaidDocument);

  private expenseTimeBillingsFetcher = new FetchHelper<
    GetExpenseTimeBillingsQuery,
    GetExpenseTimeBillingsQueryVariables
  >(GetExpenseTimeBillingsDocument);

  private pohodaExportFetcher = new FetchHelper<
    PohodaExpenseExportQuery,
    PohodaExpenseExportQueryVariables
  >(PohodaExpenseExportDocument);

  private attachmentsExportFetcher = new FetchHelper<
    ExpenseAttachmentsExportBulkQuery,
    ExpenseAttachmentsExportBulkQueryVariables
  >(ExpenseAttachmentsExportBulkDocument);

  private selectedSummaryFetcher = new FetchHelper<
    ExpensesTableSelectedSummaryQuery,
    ExpensesTableSelectedSummaryQueryVariables
  >(ExpensesTableSelectedSummaryDocument);

  @observable
  expenseTimeBillingsCache: Map<string, { hasLockedTimeBillings: boolean }> =
    new Map();

  @computed
  get isCheckingTimeBillings() {
    return this.expenseTimeBillingsFetcher.isLoading.value;
  }

  @computed
  get isOmegaExportDisabled() {
    return this.selectedExpensesIds.length === 0;
  }

  @computed
  get isOmegaExporting() {
    return this.omegaExportFetcher.isLoading.value;
  }

  @computed
  get isPremierExporting() {
    return this.premierExportFetcher.isLoading.value;
  }

  @computed
  get isAbraFlexiExporting() {
    return this.abraFlexiExportFetcher.isLoading.value;
  }

  @computed
  get isBulkDeleting() {
    return this.bulkDeleteMutator.isLoading.value;
  }

  @computed
  get isMarkingAsUnpaid(): boolean {
    return this.markAsUnpaidMutator.isLoading.value;
  }

  @action.bound
  async markAsUnpaid(removeMatchedPayments: boolean): Promise<void> {
    const id = this.markAsUnpaidModal.additionalData?.id;
    if (!id) return;

    const [, error] = await this.markAsUnpaidMutator.mutate({
      id,
      remove_matched_payments: removeMatchedPayments,
    });
    if (error) return;

    this.refetch?.();
  }

  @action.bound
  async exportOmega() {
    const [data, error] = await this.omegaExportFetcher.fetch({
      ids: this.selectedExpensesIds,
    });

    if (error || !data.omegaExpenseExport) return;

    this.refetch?.();
    window.location.href = data.omegaExpenseExport;
  }

  @action.bound
  async exportAbraFlexi() {
    const [data, error] = await this.abraFlexiExportFetcher.fetch({
      expense_ids: this.selectedExpensesIds,
    });

    if (error || !data.expenseAbraFlexiExport) return;

    appStore.UIStore.toast({
      title: t`ABRA export is being prepared. Check your data in ABRA in a few minutes.`,
      status: "success",
    });
  }

  @action.bound
  async exportPremier() {
    const [data, error] = await this.premierExportFetcher.fetch({
      expense_ids: this.selectedExpensesIds,
    });

    if (error || !data.expensePremierExport) return;

    const exportResponse = data.expensePremierExport.trim().toLowerCase();

    if (exportResponse === "ok") {
      appStore.UIStore.toast({
        title: t`Premier exported successfully`,
        status: "success",
      });
      return;
    }

    if (exportResponse === "error") {
      appStore.UIStore.toast({ status: "error" });
      return;
    }

    window.location.href = data.expensePremierExport;
  }

  constructor() {
    makeObservable(this);
  }

  @computed
  get isSelectedSummaryLoading() {
    return this.selectedSummaryFetcher.isLoading.value;
  }

  @action.bound
  setSelectedIds(ids: ExpenseModel["id"][]) {
    const dedupedIds = Array.from(new Set(ids));
    this.selectedExpensesIds = dedupedIds;

    this.selectedWhere.filtersByColumn
      .get(ExpensesWhereColumn.Id)
      ?.setValue(dedupedIds);
  }

  @action.bound
  private async fetchSelectedSummary(): Promise<void> {
    if (this.selectedExpensesIds.length === 0) {
      this.selectedSummary = undefined;
      return;
    }

    const [data, error] = await this.selectedSummaryFetcher.fetch({
      filters: {
        where: this.selectedWhere.asGraphQueryParam,
      },
    });

    if (error || !data.expensesSummary) return;

    this.selectedSummary = data.expensesSummary;
  }

  @action
  clearExpenseIds(selectedIds: ExpenseModel["id"][]) {
    this.initialSelectedIds = selectedIds;
  }

  fromGetExpensesQuery(data: GetExpensesQuery) {
    this.expenses = (data.expenses?.data ?? []).map((i) => new ExpenseModel(i));
    if (data.expenses) {
      this.pagination.setFromPaginatorInfo(data.expenses.paginatorInfo);
    }
  }

  @action
  setFilterOptions(data: ExpenseFilterOptionsQuery) {
    this.userSimpleMap = data.userSimpleMap;
    this.cardOptions = mapToOptions.toOptions(
      data.paymentTypes.filter(
        (i) => i.system_name === ExpensePaymentTypeEnum.Card,
      ),
      "name",
    );

    this.bankAccountOptions = mapToOptions.toOptions(
      data.paymentTypes.filter(
        (i) => i.system_name === ExpensePaymentTypeEnum.BankTransfer,
      ),
      "name",
    );

    this.modalForms.bank_account_id.onChange(this.bankAccountOptions[0]?.value);
    this.modalForms.card_id.onChange(this.cardOptions[0]?.value);

    this.where.filtersByColumn.get(ExpensesWhereColumn.MandatoryId)?.setOptions(
      data.userSimpleMap.map(({ id, full_name }) => ({
        value: id,
        label: full_name,
      })),
    );

    this.where.filtersByColumn.get(ExpensesWhereColumn.BrandId)?.setOptions(
      data.clientsSimpleMap.map((i) => ({
        value: i?.id,
        label: i?.name,
        options: i?.brands.map((brand) => {
          return {
            label: brand.name,
            value: brand.id,
          };
        })!,
      })),
    );

    this.where.filtersByColumn
      .get(ExpensesWhereColumn.WorkspaceCompanyId)
      ?.setOptions(
        mapToOptions.workspaceCompanies(appStore.workspaceStore.companies),
      );

    this.where.filtersByColumn
      .get(ExpensesWhereColumn.WorkspaceBankAccountId)
      ?.setOptions(
        appStore.workspaceStore.companies
          .flatMap(({ bankAccounts }) => bankAccounts)
          .filter(({ deleted }) => !deleted)
          .map(({ id, iban }) => ({ value: id, label: iban })),
      );

    this.where.filtersByColumn
      .get(ExpensesWhereColumn.ProjectId)
      ?.setOptions(mapToOptions.projects(data.projectsSimpleMap));
  }

  @computed
  get activeQuickFilter() {
    return this.where.filtersByColumn.get(ExpensesWhereColumn.Status);
  }

  @computed
  get searchParams() {
    return commonSearchParams(this);
  }

  @action.bound
  setStatusFilter(value: ExpenseStatusEnum) {
    this.activeQuickFilter?.setValue(value);
    this.setActiveView("default");
  }

  @computed
  get queryParams() {
    return commonQueryVariables(this);
  }

  static create() {
    return new StoreExpenses();
  }

  @action.bound
  async exportToPohoda(expenseIds: string[]) {
    const [data, error] = await this.pohodaExportFetcher.fetch({
      expense_ids: expenseIds,
    });

    if (error || !data?.pohodaExpenseExport) return;

    window.open(data.pohodaExpenseExport, "_blank")?.focus();
  }

  @action.bound
  async downloadAttachmentsBulkAction(expenseIds: string[]) {
    this.bulkActionsLoading = true;

    const [data, error] = await this.attachmentsExportFetcher.fetch({
      selectedIds: expenseIds,
    });

    if (error || !data?.ExpenseAttachmentsExport) {
      this.bulkActionsLoading = false;
      return;
    }

    window.open(data.ExpenseAttachmentsExport, "_blank")?.focus();

    this.bulkActionsLoading = false;
  }

  @action.bound
  async exportToVario(expenseIds: string[]) {
    const [data, error] = await this.varioExportFetcher.fetch({
      expense_ids: expenseIds,
    });

    if (!data?.varioExpenseExport || error) return;

    window.open(data.varioExpenseExport, "_blank");
  }

  @action.bound
  async exportToTransakcio(expenseIds: string[]) {
    const [data, error] = await this.transakcioExportFetcher.fetch({
      expense_ids: expenseIds,
    });

    if (!data?.transakcioExpenseExport || error) return;

    window.open(data.transakcioExpenseExport, "_blank");
  }

  @action.bound
  async bulkDeleteExpenses(expenseIds: string[]) {
    const [data, error] = await this.bulkDeleteMutator.mutate({
      ids: expenseIds,
    });

    if (error || !data?.bulkDeleteExpenses) return false;

    appStore.UIStore.toast({
      status:
        data.bulkDeleteExpenses.successful === data.bulkDeleteExpenses.total
          ? "success"
          : "warning",
      title: t`${data.bulkDeleteExpenses.successful} out of ${data.bulkDeleteExpenses.total} expenses deleted successfully`,
    });

    this.refetch?.();
    this.setSelectedIds([]);

    return true;
  }

  @action.bound
  async fetchExpenseTimeBillings(expenseId: string) {
    const [data, error] = await this.expenseTimeBillingsFetcher.fetch({
      id: expenseId,
    });

    if (error || !data?.expense) {
      this.expenseTimeBillingsCache.set(expenseId, {
        hasLockedTimeBillings: false,
      });
      return;
    }

    const hasLockedTimeBillings = data.expense.expenseItems.some(
      ({ timeBillings }) => timeBillings.some(({ is_locked }) => is_locked),
    );

    this.expenseTimeBillingsCache.set(expenseId, {
      hasLockedTimeBillings,
    });
  }

  @action.bound
  async bulkLockExpense(ids: string[]) {
    const [data, error] = await appStore.modelLock.bulkLockModel(
      ids,
      ModelLockLockableTypeEnum.Expense,
    );

    if (error || !data?.bulkLockModel) return false;

    appStore.UIStore.toast({
      status: data.bulkLockModel.failed === 0 ? "success" : "warning",
      title: t`${data.bulkLockModel.total - data.bulkLockModel.failed} out of ${data.bulkLockModel.total} expenses locked successfully`,
    });

    this.refetch?.();
    this.setSelectedIds([]);

    return true;
  }

  @action.bound
  async bulkUnlockExpenses(ids: string[]) {
    const [data, error] = await appStore.modelLock.bulkUnlockModel(
      ids,
      ModelLockLockableTypeEnum.Expense,
    );

    if (error || !data?.bulkUnlockModel) return false;

    appStore.UIStore.toast({
      status: data.bulkUnlockModel.failed === 0 ? "success" : "warning",
      title: t`${data.bulkUnlockModel.total - data.bulkUnlockModel.failed} out of ${data.bulkUnlockModel.total} expenses unlocked successfully`,
    });

    this.refetch?.();
    this.setSelectedIds([]);

    return true;
  }

  @action.bound
  async lockExpense(id: string) {
    const [data, error] = await appStore.modelLock.lockModel(
      id,
      ModelLockLockableTypeEnum.Expense,
    );

    if (error || !data?.lockModel) return false;

    appStore.UIStore.toast({
      status: "success",
      title: t`Expense locked successfully`,
    });

    this.refetch?.();

    return true;
  }

  @action.bound
  async unlockExpense(id: string) {
    const [data, error] = await appStore.modelLock.unlockModel(
      id,
      ModelLockLockableTypeEnum.Expense,
    );

    if (error || !data?.unlockModel) return false;

    appStore.UIStore.toast({
      status: "success",
      title: t`Expense unlocked successfully`,
    });

    this.refetch?.();

    return true;
  }
}
